import { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { subscribeToUserProfile } from '../firebase/firestore';

/**
 * Seller Status Hook
 *
 * Tracks the current user's Stripe Connect onboarding status.
 * Used to determine if they can create raffles.
 *
 * Status values:
 * - 'loading' - Still fetching user data
 * - 'unauthenticated' - User not logged in
 * - 'unverified' - User hasn't verified phone
 * - 'not_started' - No Stripe Connect account
 * - 'pending' - Connect account created but onboarding incomplete
 * - 'active' - Fully onboarded, can receive payouts
 * - 'restricted' - Account has restrictions (needs attention)
 */
export function useSellerStatus() {
  const { user, userProfile, loading: authLoading } = useAuth();
  const [status, setStatus] = useState('loading');
  const [connectAccount, setConnectAccount] = useState(null);

  useEffect(() => {
    // Still loading auth
    if (authLoading) {
      setStatus('loading');
      return;
    }

    // Not logged in
    if (!user) {
      setStatus('unauthenticated');
      setConnectAccount(null);
      return;
    }

    // Phone not verified (skip in emulator mode)
    const isEmulator = import.meta.env.VITE_USE_EMULATORS === 'true';
    if (!isEmulator && !userProfile?.phoneVerified) {
      setStatus('unverified');
      setConnectAccount(null);
      return;
    }

    // Check Stripe Connect status from user profile
    const stripeStatus = userProfile?.stripeConnectStatus;
    const stripeAccountId = userProfile?.stripeConnectId;

    if (!stripeAccountId) {
      setStatus('not_started');
      setConnectAccount(null);
      return;
    }

    // Has Connect account - set status based on their state
    setConnectAccount({
      id: stripeAccountId,
      status: stripeStatus,
      chargesEnabled: userProfile?.stripeChargesEnabled || false,
      payoutsEnabled: userProfile?.stripePayoutsEnabled || false,
    });

    if (stripeStatus === 'active' && userProfile?.stripeChargesEnabled) {
      setStatus('active');
    } else if (stripeStatus === 'restricted') {
      setStatus('restricted');
    } else {
      setStatus('pending');
    }
  }, [user, userProfile, authLoading]);

  return {
    status,
    connectAccount,
    isLoading: status === 'loading',
    canCreateRaffle: status === 'active',
    needsOnboarding: status === 'not_started' || status === 'pending' || status === 'restricted',
    needsAuth: status === 'unauthenticated',
    needsPhoneVerification: status === 'unverified',
  };
}

/**
 * Check if user has an active raffle
 *
 * Users can only have one active raffle at a time.
 * This hook checks if they already have one.
 */
export function useHasActiveRaffle() {
  const { user } = useAuth();
  const [hasActive, setHasActive] = useState(false);
  const [activeRaffleId, setActiveRaffleId] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!user) {
      setHasActive(false);
      setActiveRaffleId(null);
      setLoading(false);
      return;
    }

    // Subscribe to user profile to check activeRaffleId
    const unsubscribe = subscribeToUserProfile(user.uid, (profile) => {
      if (profile?.activeRaffleId) {
        setHasActive(true);
        setActiveRaffleId(profile.activeRaffleId);
      } else {
        setHasActive(false);
        setActiveRaffleId(null);
      }
      setLoading(false);
    });

    return () => unsubscribe();
  }, [user]);

  return { hasActive, activeRaffleId, loading };
}

export default useSellerStatus;
